import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { StaticImageData } from "next/image";
import { useTranslations } from "next-intl";

interface ServiceCardProps {
  title: string;
  description: string;
  image: string | StaticImageData;
  link: string;
  size?: "large" | "medium" | "small";
  index: number;
}

const ServiceCard = ({ 
  title, 
  description, 
  image, 
  link, 
  size = "medium",
  index 
}: ServiceCardProps) => {
  const t = useTranslations();
  
  const sizeClasses = {
    large: "col-span-1 sm:col-span-2 lg:col-span-2 row-span-1 sm:row-span-2",
    medium: "col-span-1 row-span-1",
    small: "col-span-1 row-span-1",
  };

  const heightClasses = {
    large: "h-[280px] sm:h-[320px] md:h-[400px] lg:h-[500px]",
    medium: "h-[240px] sm:h-[280px] md:h-[240px]",
    small: "h-[200px] sm:h-[240px]",
  };

  return (
    <motion.a
      href={link}
      className={`${sizeClasses[size]} ${heightClasses[size]} relative group overflow-hidden rounded-xl sm:rounded-2xl cursor-pointer block touch-manipulation`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1, ease: "easeOut" }}
      viewport={{ once: true, margin: "-50px" }}
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center transition-transform duration-700 ease-out group-hover:scale-110 group-active:scale-105"
        style={{ backgroundImage: `url(${typeof image === 'string' ? image : image.src})` }}
      />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-foreground/90 via-foreground/40 to-transparent opacity-70 group-hover:opacity-80 transition-opacity duration-500" />
      
      {/* Decorative accent line */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-accent to-primary/50 transform -translate-x-full group-hover:translate-x-0 transition-transform duration-700" />
      
      {/* Content */}
      <div className="absolute inset-0 p-4 sm:p-5 md:p-6 flex flex-col justify-end">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: index * 0.1 + 0.2 }}
          viewport={{ once: true }}
        >
          {/* Category Tag */}
          <span className="inline-block px-2 sm:px-3 py-1 text-xs font-medium tracking-wider uppercase bg-primary/20 text-primary-foreground backdrop-blur-sm rounded-full mb-2 sm:mb-3 border border-primary/30">
            {t('services.explore')}
          </span>
          
          {/* Title */}
          <h3 className={`font-serif font-bold text-primary-foreground mb-2 leading-tight ${
            size === "large" 
              ? "text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl" 
              : "text-base sm:text-lg md:text-xl lg:text-2xl"
          }`}>
            {title}
          </h3>
          
          {/* Description */}
          <p className={`text-primary-foreground/80 font-light leading-relaxed mb-3 sm:mb-4 ${
            size === "large" 
              ? "text-sm sm:text-base md:text-lg max-w-md line-clamp-3" 
              : "text-xs sm:text-sm line-clamp-2"
          }`}>
            {description}
          </p>
          
          {/* CTA Button */}
          <div className="flex items-center gap-2 text-primary-foreground group/btn">
            <span className="text-xs sm:text-sm font-medium tracking-wide">{t('common.viewAll')}</span>
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary/80 flex items-center justify-center group-hover:bg-primary transition-colors duration-300 flex-shrink-0">
              <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 text-primary-foreground transform group-hover/btn:translate-x-0.5 transition-transform duration-300" />
            </div>
          </div>
        </motion.div>
      </div>
      
      {/* Hover glow effect */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
        <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-primary/20 to-transparent" />
      </div>
      
      {/* Touch feedback overlay */}
      <div className="absolute inset-0 bg-white/10 opacity-0 group-active:opacity-100 transition-opacity duration-150 pointer-events-none" />
    </motion.a>
  );
};

export default ServiceCard;
